import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Link, useNavigate, useLocation } from "react-router-dom";
import SnapLayerHero from "@/components/layout/SnapLayerHero";
import { Gift, Loader2, Mail, KeyRound, Eye, EyeOff, Sparkles } from "lucide-react";
import { clearStoredAuthRedirect, resolvePostLoginRedirect } from "@/utils/authRedirect";

type LoginMethod = 'password' | 'magic-link';

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState<LoginMethod>('password');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const redirectContext = useMemo(
    () => resolvePostLoginRedirect(location.state as { from?: string; reason?: unknown } | null),
    [location.state],
  );

  const promoHubUrl = useMemo(() => {
    const configuredBase = import.meta.env.VITE_GETSNAPLAYER_URL as string | undefined;
    const fallbackBase = window.location.hostname === 'dev.snaplayer.app'
      ? 'https://launch-promo-dev--cerulean-brioche-26d233.netlify.app'
      : 'https://getsnaplayer.com';
    const url = new URL('/share', configuredBase || fallbackBase);
    const normalizedEmail = email.trim();
    if (normalizedEmail) {
      url.searchParams.set('email', normalizedEmail);
      url.searchParams.set('auto_login', 'true');
    }
    url.searchParams.set('source', 'snaplayer_app_login');
    return url.toString();
  }, [email]);

  const sessionNotice = useMemo(() => {
    if (redirectContext.reason === 'expired') {
      return {
        description: 'Your session expired. Sign in again to return to where you left off.',
        title: 'Session expired',
      };
    }

    if (redirectContext.reason === 'unauthenticated') {
      return {
        description: 'Sign in to continue to your workspace.',
        title: 'Sign in required',
      };
    }

    return null;
  }, [redirectContext.reason]);

  const onPasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      toast({ title: "Login failed", description: error.message });
    } else {
      toast({ title: "Welcome back" });
      // Redirect to intended route or dashboard
      clearStoredAuthRedirect();
      navigate(redirectContext.path, { replace: true });
    }
  };

  const onMagicLinkLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast({ title: "Email required", description: "Please enter your email address" });
      return;
    }
    
    setLoading(true);
    const redirectTo = `${window.location.origin}${redirectContext.path}`;
    const { data, error } = await supabase.functions.invoke('send-magic-link', {
      body: {
        email,
        redirectTo,
      },
    });
    setLoading(false);
    
    if (error) {
      toast({ title: "Failed to send magic link", description: error.message });
    } else if (data?.error) {
      toast({ title: "Failed to send magic link", description: data.error });
    } else {
      setMagicLinkSent(true);
      toast({ 
        title: "Check your email", 
        description: "We sent you a magic link to log in" 
      });
    }
  };

  const onForgotPassword = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!email) {
      toast({ title: "Email required", description: "Please enter your email address first" });
      return;
    }
    
    setLoading(true);
    const { data, error } = await supabase.functions.invoke('send-password-reset', {
      body: {
        email,
        redirectTo: `${window.location.origin}/reset-password`,
      },
    });
    setLoading(false);
    
    if (error) {
      toast({ title: "Failed to send reset email", description: error.message });
    } else if (data?.error) {
      toast({ title: "Failed to send reset email", description: data.error });
    } else {
      toast({ 
        title: "Password reset email sent", 
        description: "Check your inbox for a link to reset your password" 
      });
    }
  };

  if (magicLinkSent) {
    return (
      <main className="min-h-screen bg-sl-bg text-sl-text flex flex-col">
        <SnapLayerHero
          title="Check your email"
          subtitle="We've sent you a magic link to log in."
        />

        <div className="container mx-auto px-4 py-12 flex-1 flex items-start justify-center">
          <div className="w-full max-w-md">
            <div className="bg-sl-surface border border-sl-surface-2 rounded-2xl p-8 shadow-lg text-center">
              <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                <Mail className="h-8 w-8 text-green-400" />
              </div>
              <h2 className="text-xl font-bold mb-4 text-white">Magic Link Sent!</h2>
              <p className="text-slate-300 mb-6">
                We sent a magic link to <strong className="text-white">{email}</strong>. 
                Click the link in the email to log in.
              </p>
              <button
                type="button"
                onClick={() => setMagicLinkSent(false)}
                className="w-full px-4 py-2.5 rounded-md border-2 border-slate-600 bg-transparent text-white hover:bg-slate-700 hover:border-slate-500 transition-colors font-medium text-sm"
                style={{ color: '#e2e8f0', borderColor: '#475569' }}
              >
                Use a different email
              </button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-sl-bg text-sl-text flex flex-col">
      <SnapLayerHero
        title="Sign in to SnapLayer"
        subtitle="Access your projects, templates, and license details."
      />

      <div className="container mx-auto px-4 py-12 flex-1 flex items-start justify-center">
        <div className="w-full max-w-md">
          <div className="bg-sl-surface border border-sl-surface-2 rounded-2xl p-8 shadow-lg">
            <h1 className="text-2xl font-bold mb-6 text-center text-white">Login</h1>

            {sessionNotice && (
              <div className="mb-6 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-left">
                <p className="text-sm font-semibold text-amber-200">{sessionNotice.title}</p>
                <p className="mt-1 text-sm text-amber-100/90">{sessionNotice.description}</p>
              </div>
            )}

            <div className="mb-6 rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-400/20 text-emerald-200">
                  <Gift className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">
                    LTD Launch Credit Opportunity - Share about SnapLayer and earn extra Monthly Credits!
                  </p>
                  <p className="mt-1 text-xs text-emerald-100/90">
                    Sign in, grab your account link, and try guided feature demos for bonus credit opportunities.
                  </p>
                  <a
                    href={promoHubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-emerald-200 hover:text-emerald-100 hover:underline"
                  >
                    Open Play/Demo/Share hub <Sparkles className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            </div>
            
            {/* Login method toggle */}
            <div className="flex gap-2 mb-6 p-1 bg-sl-bg rounded-lg">
              <button
                type="button"
                onClick={() => setMethod('password')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  method === 'password' 
                    ? 'bg-sl-surface-2 text-white' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <KeyRound className="h-4 w-4" />
                Password
              </button>
              <button
                type="button"
                onClick={() => setMethod('magic-link')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  method === 'magic-link' 
                    ? 'bg-sl-surface-2 text-white' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Mail className="h-4 w-4" />
                Magic Link
              </button>
            </div>

            {method === 'password' ? (
              <form onSubmit={onPasswordLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-slate-200">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-sl-bg border-sl-surface-2 text-white placeholder:text-slate-500"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="password" className="text-slate-200">Password</Label>
                    <button
                      type="button"
                      onClick={onForgotPassword}
                      className="text-sm text-blue-400 hover:text-blue-300 hover:underline font-medium transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="off"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="bg-sl-bg border-sl-surface-2 text-white placeholder:text-slate-500 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 text-base font-semibold bg-sl-accent hover:bg-sl-accent/90"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Logging in...
                    </>
                  ) : (
                    "Login"
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={onMagicLinkLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email-magic" className="text-slate-200">Email</Label>
                  <Input
                    id="email-magic"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    required
                    className="bg-sl-bg border-sl-surface-2 text-white placeholder:text-slate-500"
                  />
                </div>
                <p className="text-sm text-slate-300">
                  We'll send you an email with a magic link to log in instantly.
                </p>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 text-base font-semibold bg-sl-accent hover:bg-sl-accent/90"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    "Send Magic Link"
                  )}
                </Button>
              </form>
            )}

            <div className="mt-6 pt-6 border-t border-sl-surface-2">
              <p className="text-sm text-slate-300 text-center mb-3">
                Need to Activate Your SnapLayer License(s)?{" "}
                <Link
                  to="/activation"
                  className="text-blue-400 hover:text-blue-300 hover:underline font-medium transition-colors"
                >
                  License/Account Activation
                </Link>
              </p>
              <p className="text-xs text-slate-400 text-center mb-4">
                If you have already activated license(s) and created your account, and want to add more licenses, login then activate in the dashboard.
              </p>
            </div>

            <p className="mt-4 text-sm text-slate-300 text-center">
              Don't have an account?{" "}
              <a
                href="https://getsnaplayer.com"
                target="_blank"
                rel="noreferrer"
                className="text-blue-400 hover:text-blue-300 hover:underline font-medium transition-colors"
              >
                Purchase the Lifetime deal here.
              </a>
            </p>
            <p className="mt-2 text-xs text-slate-400 text-center">
              Need help accessing your account?{" "}
              <a
                href="mailto:support@getsnaplayer.com?subject=SnapLayer%20Account%20Access%20Help"
                className="text-blue-400 hover:text-blue-300 hover:underline font-medium transition-colors"
              >
                Email support
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Login;
